import React, { useState, useEffect } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { transition, tabTransition, containerVariants, overlayVariants, contentVariants, tabVariants, qrVariants, closeVariants } from './variants';
import { auth } from '../../firebase/firebaseConfig';
import { Link, useMatch } from 'react-router';
import { useAuth } from '../../utils/Auth';
import { useFetchLqr } from '../../utils/hooks/useFetchLqr';
import { useFetchQrImage } from '../../utils/hooks/useFetchQrImage';
import { useQuery } from '../../utils/hooks/useQuery';
import adminAPI from '../../utils/API/adminAPI';
import API from '../../utils/API';
import styles from './QrShare.module.css';

const fallbackCopyToClipboard = (text) => {
    var textArea = document.createElement("textarea");
    textArea.value = text;

    textArea.style.top = "0";
    textArea.style.left = "0";
    textArea.style.position = "fixed";

    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    document.body.removeChild(textArea);
};

const isMobileDevice = () => {
    const userAgent = window.navigator.userAgent.toLowerCase();
    const isMobile = /mobile/.test(userAgent);
    return isMobile;
};

const copyToClipboard = async (text) => {
    if (navigator.share && isMobileDevice()) {
        try {

            await navigator.share({
                title: 'Digital Business Cards by BitSignal',
                text: `Check out my Digital Business Card! \n\n${text}`,
            });

        } catch (error) {

            console.error('Error sharing:', error);

        };

    } else if (navigator.clipboard) {
        navigator.clipboard.writeText(text).then(function () {
        }, function (err) {
            console.error('Async: Could not copy text: ', err);
        });

    } else if (!navigator.share && !navigator.clipboard) {
        fallbackCopyToClipboard(text);
        return;
    };
};

// Main Component
export default function QrShare(props) {

    const { isDBC, showQR, setShowQR, ref } = props;
    const { route, userObj } = useAuth();
    const event = useQuery('event');
    const routeProps = useMatch('/:enterprise?/:username');
    
    const [ tabIndex, setTabIndex ] = useState(isDBC ? 0 : 1);
    const [ showCheck, setShowCheck ] = useState(false);
    // my qr stuff
    const [ myQr, setMyQr ] = useState({
        name: 'My QR',
        route: '',
        url: '',
    });
    const myQrStyle = useFetchLqr({qrId: userObj?.liquidqr?.qrId || '', route: event ? `${route}?event=${event}` : route});
    const myQrCode = useFetchQrImage(event ? `${route}?event=${event}` : route, myQrStyle);
    // their qr stuff
    const [ theirQr, setTheirQr ] = useState({
        name: '',
        route: '',
        url: '',
        qrId: ''
    });
    const theirQrStyle = useFetchLqr({qrId: theirQr.qrId, route: event ? `${routeProps?.pathname?.replace('/','')}?event=${event}` : routeProps?.pathname?.replace('/','')});
    const theirQrCode = useFetchQrImage(event ? `${routeProps?.pathname?.replace('/','')}?event=${event}` : routeProps?.pathname?.replace('/','') || '', theirQrStyle)

    useEffect(() => {
        setMyQr(prev => ({
            ...prev,
            route: event ? `${route}?event=${event}` : route,
            url: event ? `https://bitsignal.io/${route}?event=${event}` : `https://bitsignal.io/${route}`,
        }))
    },[route, event]);

    useEffect(() => {
        if (!isDBC) return;

        const fetchTheirVcard = async () => {
            const user = routeProps?.pathname.split('/').slice(1).join('@');
            try {
                const { data: { uid, liquidqr = {qrId: ''} } } = await adminAPI.getUserByUsername(user);
                const vcard = await API.getVcardByUID(uid);
                setTheirQr(prev => ({
                    ...prev,
                    name: vcard?.name_first || user.substring(user.indexOf('@') + 1),
                    route: event ? `${routeProps?.pathname?.replace('/','')}?event=${event}` : routeProps?.pathname?.replace('/','') || '',
                    url: event ? `https://bitsignal.io/${routeProps?.pathname?.replace('/','')}?event=${event}` : `https://bitsignal.io/${routeProps?.pathname?.replace('/','')}`,
                    qrId: liquidqr.qrId
                }))
            } catch (err) {
                console.error(err);
            }
        };
        fetchTheirVcard();

    },[routeProps, isDBC, event]);

    useEffect(() => {
        setTabIndex(isDBC ? 0 : 1);
    },[isDBC])

    useEffect(() => {
        if (showCheck) {
            const timer = setTimeout(() => {
                setShowCheck(false);
            }, 3000);
            return () => clearTimeout(timer);
        };
    },[showCheck]);

    return (
        <>
        <AnimatePresence initial={false}>
            {showQR && (
                <>
                <motion.div 
                    className={styles.container}
                    variants={containerVariants}
                    transition={transition}
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                    ref={ref}
                    custom={window.innerWidth}
                >
                    <div className={styles.tabContainer}>
                        {isDBC && 
                            <motion.button 
                                className={styles.tab} 
                                onClick={() => setTabIndex(0)} 
                                variants={tabVariants}
                                transition={tabTransition}
                                whileHover={tabIndex !== 0 ? 'hoverLeft' : 'active'}
                                animate={tabIndex === 0 ? "active" : "inactiveLeft"}
                            >
                                {`${theirQr.name}'s QR`}
                            </motion.button>
                        }
                        {userObj &&
                            <motion.button 
                                className={styles.tab} 
                                onClick={() => setTabIndex(1)}
                                variants={tabVariants}
                                transition={tabTransition}
                                whileHover={tabIndex !== 1 ? 'hoverRight' : 'active'}
                                animate={tabIndex === 1 ? "active" : "inactiveRight"}
                            >
                                My QR
                            </motion.button>
                        }
                    </div>
                    <div className={styles.content}>
                        <motion.div
                            className={styles.innerContent}
                            variants={contentVariants}
                            transition={transition}
                            initial="hidden"
                            animate="visible"
                        >
                            <AnimatePresence initial={false} mode='wait'>
                                {tabIndex === 0 ? (
                                    <motion.div
                                        className={styles.qrContainer}
                                        key='theirQr'
                                        variants={qrVariants}
                                        transition={{...transition, duration: .5}}
                                        initial='hidden'
                                        animate='visible'
                                        exit='exit'
                                    >
                                        <img src={theirQrCode} />
                                    </motion.div>
                                ) : (
                                    <motion.div
                                        className={styles.qrContainer}
                                        key='myQr'
                                        variants={qrVariants}
                                        transition={{...transition, duration: .5}}
                                        initial='hidden'
                                        animate='visible'
                                        exit='exit'
                                    >
                                        <img src={myQrCode} />
                                    </motion.div>
                                )}
                            </AnimatePresence>
                            <button className={styles.shareBtn} onClick={() => {!isMobileDevice() && setShowCheck(true); copyToClipboard(tabIndex === 0 ? theirQr?.url : myQr?.url)}}>{showCheck ? <i className='fa-solid fa-check' style={{pointerEvents: 'none'}} /> : <span style={{pointerEvents: 'none'}}> Share</span>}</button>
                            {auth.currentUser && <Link to='/contacts' state={{ fromDBC: true}}>Share Another Contact</Link>}
                        </motion.div>
                    </div>
                    <motion.button
                        onClick={() => setShowQR(false)}
                        className={styles.close}
                        variants={closeVariants}
                        transition={transition}
                        initial="hidden"
                        animate="visible"
                        exit="exit"
                        style={userObj ? {bottom: '27rem'} : {bottom: '25.25rem'}}
                    >
                        <i className='far fa-xmark' style={{pointerEvents: 'none'}} />
                    </motion.button>
                </motion.div>
                <motion.div 
                    className={styles.overlay}
                    variants={overlayVariants}
                    transition={transition}
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                    custom={window.innerWidth}
                />
                </>
            )}
        </AnimatePresence>
        </>
    );
};